'use client';

import React, { useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/navigation';
import { localeNames, locales } from "@/config/i18n";
import { cn } from '@/lib/utils';

/**
 * Footer 语言链接组件
 * 显示所有可用语言的链接列表
 */
const FooterLanguageLinks = ({ className = '', linkClassName = '' }) => {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  // 获取当前路径去除语言前缀的函数
  const getCurrentPathWithoutLocale = useCallback(() => {
    const currentPath = window.location.pathname;
    
    // 检查路径是否以语言代码开头
    for (const loc of locales) {
      if (currentPath === `/${loc}`) {
        return '/';
      }
      if (currentPath.startsWith(`/${loc}/`)) {
        return currentPath.substring(loc.length + 1);
      }
    }
    
    // 如果没有语言前缀，返回原路径
    return currentPath;
  }, []);

  // 处理语言切换
  const handleLanguageChange = useCallback((newLocale) => {
    try {
      // 标准的 next-intl 路由切换
      router.replace(pathname, { locale: newLocale });
    } catch (error) {
      console.error('Language switch error:', error);
      // 如果出错，使用window.location作为后备
      try {
        const pathWithoutLocale = getCurrentPathWithoutLocale();
        const newPath = newLocale === 'en' ? pathWithoutLocale : `/${newLocale}${pathWithoutLocale}`;
        window.location.href = newPath;
      } catch (fallbackError) {
        console.error('Fallback navigation failed:', fallbackError);
        window.location.reload();
      }
    }
  }, [router, pathname, getCurrentPathWithoutLocale]);

  return (
    <div className={cn("flex flex-wrap justify-center gap-2 py-4", className)}>
      {locales.map((localeCode) => {
        const isActive = locale === localeCode;
        
        return (
          <button
            key={localeCode}
            onClick={() => handleLanguageChange(localeCode)}
            className={cn(
              "text-sm transition-all duration-200 hover:scale-105",
              isActive 
                ? "text-green-400 font-medium" 
                : "text-gray-300 hover:text-green-300", // 修复对比度：从text-gray-500改为text-gray-300
              linkClassName
            )}
            title={`Switch to ${localeNames[localeCode]}`}
          >
            {localeNames[localeCode]}
          </button>
        );
      })}
    </div>
  );
};

export default FooterLanguageLinks; 